import React, { useState } from "react";

/* ============================================================
   WorldEraComposerWidget (Drag & Drop Version)
   ============================================================ */

export default function WorldEraComposerWidget({
  years = ["1600 - 1750", "1750 - 1800", "1800 - 1900", "1900 - now"],
  composers = [],
  composerSlots = 2,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const [dragging, setDragging] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);

  const parts = ["part1", "part2", "part3", "part4"];
  const isReview = mode === "review";

  function normalizeComposers(list = []) {
    const next = [...list];
    while (next.length < composerSlots) next.push("");
    return next.slice(0, composerSlots);
  }

  function updatePart(partId, patch) {
    if (mode !== "edit") return;

    const prev = value[partId] || {};
    const { composers: patchComposers, ...restPatch } = patch;

    const normalized = normalizeComposers(
      patchComposers ?? prev.composers
    );

    onChange({
      ...value,
      [partId]: {
        ...prev,
        ...restPatch,
        composers: normalized
      }
    });
  }

  // Remove composer from all slots (prevents duplicates)
  function removeComposerFromAll(name) {
    const next = { ...value };

    parts.forEach((pid) => {
      const part = next[pid] || {};
      const list = normalizeComposers(part.composers).map((c) =>
        c === name ? "" : c
      );

      next[pid] = {
        ...part,
        composers: list
      };
    });

    onChange(next);
  }

  // Collect all used composers
  const usedComposers = parts.flatMap((pid) =>
    normalizeComposers(value[pid]?.composers)
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "240px 1fr",
        gap: 32,
        alignItems: "start"
      }}
    >
      {/* ───────────── LEFT SIDE ───────────── */}
      <div style={{ fontSize: 14 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>ERAS</div>
        <ul style={{ paddingLeft: 16, marginBottom: 16 }}>
          <li>Baroque</li>
          <li>Classical</li>
          <li>Romantic</li>
          <li>Contemporary</li>
        </ul>

        <div style={{ fontWeight: 700, marginBottom: 6 }}>
          DRAG COMPOSERS
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          {composers.map((c) => {
            const alreadyUsed = usedComposers.includes(c);

            return (
              <div
                key={c}
                draggable={!alreadyUsed && mode === "edit"}
                onDragStart={() => setDragging(c)}
                style={{
                  padding: "6px 8px",
                  borderRadius: 6,
                  background: alreadyUsed ? "#ddd" : "#f3f3f3",
                  opacity: alreadyUsed ? 0.5 : 1,
                  cursor:
                    alreadyUsed || isReview ? "default" : "grab",
                  border: "1px solid #ccc",
                  userSelect: "none"
                }}
              >
                {c}
              </div>
            );
          })}
        </div>
      </div>

      {/* ───────────── MAIN GRID ───────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 24
        }}
      >
        {parts.map((pid, i) => {
          const part = value[pid] || {};
          const composersList = normalizeComposers(part.composers);
          //const fb = feedback?.parts?.[pid];
          const fb = feedback?.parts?.[pid];

          const cardBorder =
            !isReview
              ? "1px solid #ccc"
              : fb?.correct
              ? "2px solid #2e7d32"
              : "2px solid #c62828";

          const inputBorder = (ok) =>
            !isReview
              ? "1px solid #aaa"
              : ok
              ? "2px solid #2e7d32"
              : "2px solid #c62828";

          return (
            <div
              key={pid}
              style={{
                border: cardBorder,
                borderRadius: 8,
                padding: 16
              }}
            >
              {/* YEAR */}
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                {years[i] ?? ""}
              </div>

              {/* ERA DROPDOWN */}
              <div style={{ marginBottom: 12 }}>
                Era
                <select
                  value={part.era || ""}
                  disabled={isReview}
                  onChange={(e) =>
                    updatePart(pid, { era: e.target.value })
                  }
                  style={{
                    marginLeft: 8,
                    border: inputBorder(fb?.era?.correct)
                  }}
                >
                  <option value="">Select</option>
                  <option value="Baroque">Baroque</option>
                  <option value="Classical">Classical</option>
                  <option value="Romantic">Romantic</option>
                  <option value="Contemporary">Contemporary</option>
                </select>
              </div>

              {/* COMPOSER DROP SLOTS */}
              <div>
                Composer
                {composersList.map((name, idx) => {
                  //const composerFb = fb?.composers?.[idx];
                  const composerFb = fb?.composers?.slots?.[idx];

                  return (
                    <div
                      key={idx}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setHoveredSlot(`${pid}-${idx}`);
                      }}
                      onDragLeave={() => {
                        setHoveredSlot(null);
                      }}
                      onDrop={() => {
                        if (!dragging) return;

                        removeComposerFromAll(dragging);

                        const next = [...composersList];
                        next[idx] = dragging;

                        updatePart(pid, { composers: next });
                        setDragging(null);
                        setHoveredSlot(null);
                      }}
                      onClick={() => {
                        if (isReview) return;
                        if (!name) return;

                        const next = [...composersList];
                        next[idx] = "";
                        updatePart(pid, { composers: next });
                      }}
                      style={{
                        width: 140,
                        height: 32,
                        marginLeft: 8,
                        marginTop: 6,
                        // border:
                        //   hoveredSlot === `${pid}-${idx}`
                        //     ? "2px solid #1976d2" // 🔵 blue hover
                        //     : inputBorder(composerFb?.correct),
                        border: "2px solid",
                        borderColor:
                          hoveredSlot === `${pid}-${idx}`
                            ? "#1976d2"
                            : isReview
                            ? composerFb?.correct
                              ? "#2e7d32"
                              : "#c62828"
                            : "#aaa",
                        borderRadius: 6,
                        background: name ? "#f9f9f9" : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0 8px",
                        fontSize: 13,
                        cursor: name && !isReview ? "pointer" : "default",
                        transition: "border 0.15s ease"
                      }}
                    >
                      <span>{name || "Drop here"}</span>

                      {name && !isReview && (
                        <span
                          style={{
                            fontWeight: 700,
                            color: "#999",
                            marginLeft: 6
                          }}
                        >
                          ×
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}